import React, { memo, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import randomcolor from 'randomcolor';
import { Button, ButtonGroup, Form, Input } from 'reactstrap';
import moment from 'moment';
import { deleteOrder, updateOrder, updateOrders } from '../../websocket';
import { format, trimDate } from '../../helper';
import { inWarehouse } from '../WarehouseForm';
import '../../css/listorder.css';

const ROUNDING = 10;
const PERCENT = 100;

function getCountryColor(country) {
	if(country === 'GB') {
		return 'red';
	}
	return randomcolor({ seed: country });
}

function requiresNewStickers(abc) {
	return abc.some(order => {
		return order.lastPrinted && order.lastPalletUpdate && order.lastPrinted < order.lastPalletUpdate;
	});
}

function updateListener(abc, key) {
	return e => updateOrders(abc.map(o => o._id), key, e.target.value);
}

function EditRemarks({ order, submit }) {
	const [remarks, setRemarks] = useState(order.remarks ?? '');
	return <Form className="d-flex" onSubmit={e => {
		e.preventDefault();
		submit(remarks);
	}}>
		<Input type="text" value={remarks} onChange={e => setRemarks(e.target.value)} autoFocus />
		<input type="submit" className="btn btn-primary" value="Save" />
		<Button color="danger" onClick={e => {
			e.preventDefault();
			submit(order.remarks);
		}}>Cancel</Button>
	</Form>;
}

function reduceLines(abc, reducer, initial) {
	return abc.reduce((value, order) => {
		return order.lines.reduce(reducer, value);
	}, initial);
}

const PROPS = ['order', 'persons', 'person', 'locations', 'freight', 'first', 'highlight', 'openModal', 'abc', 'warehouse'];
function ListOrder({ order, persons, person, locations, freight, first, highlight, openModal, abc, warehouse }) {
	const [showRemarks, setShowRemarks] = useState(false);
	const progress = {
		SUCCESS: 0,
		PARTIAL: 0,
		FAILED: 0,
		NONE: 0,
		UNKNOWN: 0
	};
	let knownPrice = 0;
	let unknownPrice = 0;
	const cs = person.role === 'CUSTOMER_SERVICE';
	const lineLocations = new Set();
	const totalItems = reduceLines(abc, (items, line) => {
		if(!inWarehouse(warehouse, line, locations)) {
			return items;
		}
		const { status, quantity, location } = line;
		if(locations[location]) {
			lineLocations.add(locations[location]);
		}
		const pickable = status !== 'NONE' || Object.keys(line.locations).length;
		if(line.item.unitPrice) {
			if(pickable) {
				knownPrice += line.item.unitPrice * line.quantity;
			} else {
				unknownPrice += line.item.unitPrice * line.quantity;
			}
		}
		if(!pickable && !cs) {
			return items;
		}
		if(pickable) {
			if(status === 'FAILED' && line.picked) {
				progress[line.comments ? 'PARTIAL' : 'SUCCESS'] += line.picked;
				progress.FAILED += quantity - line.picked;
			} else {
				progress[status] += quantity;
			}
		} else {
			progress.UNKNOWN += quantity;
		}
		return items + quantity;
	}, 0);
	const classes = ['order'];
	if(order.code.startsWith('B2C')) {
		classes.push('b2c');
	}
	if(highlight) {
		classes.push('highlight');
	}
	if(order.noStickers) {
		classes.push('no-stickers');
	}
	if(first) {
		classes.push('first');
	}
	const teamLeader = persons.find(({ _id }) => _id === order.teamLeader);
	const pickers = persons.filter(({ _id }) => order.pickers.includes(_id));
	const packager = order.packager && persons.find(({ _id }) => _id === order.packager);
	const shipmentDate = new Date(order.shipmentDate);
	const express = abc.some(o => o.delivery?.startsWith('TARGOSPEED')) ? <strong className="text-danger">EXPRESS DELIVERY</strong> : null;
	const prio = (delta) => {
		const { _id, priority } = order;
		return e => {
			e.preventDefault();
			updateOrder(_id, {
				priority: priority + delta
			});
		};
	};
	const selectPickers = () => {
		return e => {
			const update = {
				pickers: [...e.target.options].filter(({ selected }) => selected).map(({ value }) => value).filter(Boolean),
				teamLeader: person._id
			};
			updateOrders(abc.map(o => o._id), update);
		};
	};
	const toTomorrow = (e) => {
		e.preventDefault();
		const { _id, dateOverride, priority } = order;
		updateOrder(_id, {
			dateOverride: trimDate(moment(dateOverride).add(1, 'd'))
		});
		if(priority < 1) {
			updateOrder(_id, { priority: 1 });
		}
	};
	const toggleNoStickers = (e) => {
		e.preventDefault();
		const { _id, noStickers } = order;
		updateOrder(_id, {
			noStickers: !noStickers
		});
	};
	return <li className={classes.join(' ')}>
		<Link to={`/order/${order._id}`} className="link-tile" id={order._id}>
			<div className="row">
				<h4 className={abc.length > 1 && !express ? 'col-sm-12' : 'col-sm-6'}>
					{order.dropship && person.role !== 'PACKAGER' && !cs ? 'Dropship order' : abc.map(({ code }) => code).join(' ')}
					{order.pickers.length > 1 && ' (multi)'}
					{!!order.interesting && <>{' '}<i className="fas fa-bullhorn" title="Aandacht vereist"></i></>}
					{requiresNewStickers(abc) && <>{' '}<i className="fas fa-exclamation-triangle" title="Nieuwe stickers nodig"></i></>}
				</h4>
				{!cs && <div className="col">
					{person.role === 'TEAM_LEADER' && abc.length === 1 && order.campaign} {express}
				</div>}
				{cs ? <div className="col-sm-6 text-right">
					<ButtonGroup title="Prioriteit">
						<Button size="sm" onClick={prio(-1)}><i className="fas fa-arrow-down"></i></Button>
						<Button size="sm" disabled>{order.priority}</Button>
						<Button size="sm" onClick={prio(1)}><i className="fas fa-arrow-up"></i></Button>
					</ButtonGroup>
					<Button color="default" size="sm" title="Zelf ophalen" onClick={toggleNoStickers}>
						<input type="checkbox" checked={order.noStickers} onChange={() => null} />
					</Button>
					{cs && <Button color="primary" size="sm" onClick={e => {
						e.preventDefault();
						setShowRemarks(true);
					}} title="Opmerkingen" disabled={showRemarks}><i className="fas fa-comment"></i></Button>}
					<Button color="primary" size="sm" onClick={openModal} title="Datum"><i className="fas fa-calendar-alt"></i></Button>
					<Button color="primary" size="sm" onClick={toTomorrow} title="Morgen"><i className="fas fa-arrow-right"></i></Button>
					{order.status === 'NONE' && <Button color="danger" size="sm" onClick={e => {
						e.preventDefault();
						const { _id, code } = order;
						if(window.confirm(`Weet u zeker dat u ${code} wilt verwijderen?`)) {
							deleteOrder(_id);
						}
					}} title="Delete"><i className="fas fa-times"></i></Button>}
				</div> : order.dropship && person.role === 'PACKAGER' && <div className="col-sm-6 text-right">{order.customer.code} {order.customer.name}</div>}
			</div>
			<div className="row">
				<div className={cs ? 'col-md-4' : 'col-md-5'}>
					{(!order.dropship || person.role === 'PACKAGER') && `${order.customer.code} ${order.customer.name}`}
				</div>
				<div className="col-md-3 text-center">
					{totalItems} items
					{(knownPrice > 0 || unknownPrice > 0) && <>
						, {format('EUR', knownPrice)}
						{unknownPrice > 0 && <span className="color-unknown"> + {format('EUR', unknownPrice)}</span>}
					</>}
				</div>
				{cs && <div className="col-md-2">
					{order.campaign || ''} {express}
				</div>}
				<div className={cs ? 'col-md-3 text-right' : 'col-md-4 text-right'}>
					{`${shipmentDate.toLocaleString()} `}
					<span className="country" style={{ backgroundColor: getCountryColor(order.address.country) }}>{order.address.country}</span>
				</div>
			</div>
			<>
				{[...new Set(abc.map(o => o.remarks).filter(Boolean))].map(remark => {
					return <div key={remark} className="font-weight-bold text-danger"><u>{remark}</u></div>;
				})}
			</>
			{cs && <div className="row">
				<div className="col-md-3">
					Warehouse: <strong>{[...lineLocations].join(' ')}</strong>
				</div>
				<div className="col-md-3">
					Vrachtlocatie: <strong style={{ color: randomcolor({ seed: `${order.freight}` })}}>{order.freight || '--'}</strong>
				</div>
				<div className="col-md-3">
					Teamleider: {teamLeader ? teamLeader.name : '--'}
				</div>
				<div className="col-md-3">
					Orderpicker: {pickers.length > 0 ? pickers.map(({ name }) => name).join(', ') : '--'}
					{!!packager && <>{' '}Inpakker: {packager.name}</>}
				</div>
			</div>}
		</Link>
		{showRemarks && <EditRemarks order={order} submit={remarks => {
			if(typeof remarks === 'string' && order.remarks !== remarks) {
				updateOrders(abc.map(o => o._id), 'remarks', remarks);
			}
			setShowRemarks(false);
		}} />}
		{person.role === 'TEAM_LEADER' && <div className="row">
			<div className="col-md-4">
				{['NONE', 'ON_PALLET'].includes(order.status) ? <>
					Warehouse: <strong>{[...lineLocations].join(' ')}</strong>
				</> : !!packager && <>Inpakker: {packager.name}</>}
			</div>
			<div className="col-md-4">
				{['SEALING', 'ON_PALLET'].includes(order.status) && <select
					className="form-control" value={order.freight || ''}
					onChange={updateListener(abc, 'freight')}>
					<option disabled value="">Vrachtlocatie</option>
					{freight.map(l =>
						<option key={l}>{l}</option>
					)}
				</select>}
			</div>
			<div className="col-md-4">
				{order.status !== 'DONE' && <select
					className="form-control" value={order.dropship ? order.pickers : order.pickers[0] || ''}
					multiple={order.dropship}
					onChange={selectPickers(abc)}>
					<option disabled={order.dropship} value="">Orderpicker</option>
					{persons.filter(p => p.roles.includes('PICKER')).map(p =>
						<option key={p._id} value={p._id}>{p.name}</option>
					)}
				</select>}
			</div>
		</div>}
		<div className="order-progress">
			<span className="percentage">
				{Math.round((progress.SUCCESS + progress.FAILED + progress.PARTIAL) / totalItems * ROUNDING * PERCENT) / ROUNDING}% klaar
			</span>
			{Object.keys(progress).map(key => {
				const percentage = `${Math.round(progress[key] / totalItems * ROUNDING * PERCENT) / ROUNDING}%`;
				return <div key={key} title={percentage} className={key.toLowerCase()} style={{ width: percentage }}></div>;
			})}
		</div>
	</li>;
}

export default connect(({ persons, person, locations, freight, warehouse }) => {
	return { persons, person, locations, freight, warehouse };
})(memo(ListOrder, (prev, next) => {
	return PROPS.every(key => {
		if(key === 'abc' && Array.isArray(prev.abc) && Array.isArray(next.abc)) {
			return prev.abc.length === next.abc.length && prev.abc.every((v, i) => Object.is(v, next.abc[i]));
		}
		return Object.is(prev[key], next[key]);
	});
}));
